/* RUNS, AS THE CONSOLE LISTS THEM. Every run of every version, newest first, nothing hidden and nothing ranked: the
   order is the time each was opened, never a score. */
import type { Run, RunKind, RunStatus } from '../types.ts'
import type { Refusal } from './state.ts'

/** Newest first by the time the run was opened, then by id; every run is kept. */
export function newestFirst(runs: readonly Run[]): Run[] {
  return [...runs].sort((a, b) => b.created.localeCompare(a.created) || b.id.localeCompare(a.id))
}

export const STATUS_TEXT: Record<RunStatus, string> = {
  preparing: 'Drawing the panel',
  open: 'Open',
  scoring: 'Being scored',
  complete: 'Complete',
  stopped: 'Stopped',
  failed: 'Failed',
}

export const KIND_TEXT: Record<RunKind, string> = {
  sample: 'Sample',
  train: 'Train',
  evaluation: 'Evaluation',
}

/** A run whose status can still change, so the console asks again. */
export const isLive = (s: RunStatus): boolean => s === 'preparing' || s === 'open' || s === 'scoring'
/** A run the server has scored, or scores on request: complete or stopped. A run stopped before a case was played has none (analysisPhase). */
export const hasAnalysis = (s: RunStatus): boolean => s === 'complete' || s === 'stopped'
export const canStop = (s: RunStatus): boolean => s === 'preparing' || s === 'open'
/** A run that has just closed (it was live, now it is not): it may have freed a hosted lane, so the licence's meters are read again. */
export const leftLive = (before: RunStatus | null, now: RunStatus): boolean => before !== null && isLive(before) && !isLive(now)
/** The sample runs that count against the cap on open sample runs: drawn or open, newest first. */
export const openSamples = (runs: readonly Run[]): Run[] => newestFirst(runs.filter((r) => r.kind === 'sample' && (r.status === 'preparing' || r.status === 'open')))

export type AnalysisPhase = 'none' | 'loading' | 'scoring' | 'nothing_played' | 'error' | 'ready'

/** Where a run's analysis stands. The JSON is read first: the frame and the files to keep are drawn only once it answered,
    and a run stopped before any case was played (the server's 409 nothing_played) has nothing to score, check or keep. */
export function analysisPhase(status: RunStatus, read: { data: unknown; refusal: Refusal | null }): AnalysisPhase {
  // A run being scored is read too: the server's 409 says how many cases are done, and the document shows it.
  if (status === 'scoring') return 'scoring'
  if (!hasAnalysis(status)) return 'none'
  if (read.refusal) return read.refusal.status === 409 && read.refusal.code === 'nothing_played' ? 'nothing_played' : 'error'
  return read.data === null ? 'loading' : 'ready'
}

/** A new run of the same kind, for the same version. */
export const againPath = (r: Pick<Run, 'kind' | 'agent'>): string => `/runs/new?kind=${r.kind}${r.agent ? `&agent=${encodeURIComponent(r.agent)}` : ''}`
/** The account branches a closed evaluation run's cases; a sample or train run is branched by its agent, and a run with Residents bound is not branched. */
export const canBranch = (r: Run): boolean => r.kind === 'evaluation' && r.status === 'complete' && r.residents === undefined

/** Evaluation runs that may be compared: complete, so each played its whole panel. */
export const comparable = (runs: readonly Run[]): Run[] => newestFirst(runs.filter((r) => r.kind === 'evaluation' && r.status === 'complete'))
/** Evaluation runs another version can be paired with: the server refuses a failed one. */
export const pairable = (runs: readonly Run[]): Run[] => newestFirst(runs.filter((r) => r.kind === 'evaluation' && r.status !== 'failed'))

export type CompareState =
  | { kind: 'loading' }
  | { kind: 'none' }
  | { kind: 'one'; run: Run; text: string; pairTo: string }
  | { kind: 'choose'; text: string }
  | { kind: 'same'; text: string }
  | { kind: 'ready'; a: string; b: string }

/** Where the Compare view stands: no complete evaluation run, one (pair another version with it), two to choose from, the same run twice, or two runs to compare. */
export function compareState(runs: readonly Run[] | null, a: string, b: string): CompareState {
  if (runs === null) return { kind: 'loading' }
  const options = comparable(runs)
  if (a && b && a === b) return { kind: 'same', text: `Both sides name ${a}. Choose a second run: one opened paired with ${a}, with another version.` }
  if (a && b) return { kind: 'ready', a, b }
  if (options.length === 0) return { kind: 'none' }
  if (options.length === 1) {
    const r = options[0]
    return { kind: 'one', run: r, text: `This account has one complete evaluation run, ${r.id}${r.agent ? ` of ${r.agent}` : ''}. Open a second paired with it, with another version; once it completes, the two compare case for case.`, pairTo: `/runs/new?kind=evaluation&pair=${r.id}` }
  }
  return { kind: 'choose', text: `${options.length} complete evaluation runs. Choose two; the second should have been opened paired with the first.` }
}

export type Guide = { text: string; links: { to: string; label: string }[] }

/** What a run's page says under its status, and where to go from there. `nothingPlayed` once the server said a stopped run played no case. */
export function runGuide(r: Run, opts: { nothingPlayed?: boolean } = {}): Guide {
  switch (r.status) {
    case 'preparing':
      return { text: 'Drawing the panel, about a minute. The run’s token works once the run is open.', links: [{ to: '/connect', label: 'How the agent connects' }] }
    case 'open':
      return {
        text: `Open: the agent plays it with the run’s token over MCP. It is scored when its last case is handed back or it is stopped. The token was shown once; if it is lost, stop this run and open another.`,
        links: [{ to: '/connect', label: 'How the agent connects' }, { to: againPath(r), label: 'Open another run' }],
      }
    case 'scoring':
      return { text: 'The server is scoring the cases played. The run analysis opens here when it finishes.', links: [{ to: '/runs', label: 'Every run' }] }
    case 'failed':
      return { text: `${r.reason ?? 'The run failed.'} Nothing of it counts and nothing is scored. Open another run; if it fails again, tell the operator of this server with this run’s id.`, links: [{ to: againPath(r), label: 'Open another run' }] }
    case 'stopped':
      if (opts.nothingPlayed) return { text: `Stopped in ${r.quarter} before any case was played, so there is nothing to score and no run analysis.`, links: [{ to: againPath(r), label: 'Open another run' }, { to: '/runs', label: 'Every run' }] }
      return { text: `Stopped in ${r.quarter}. The cases played are scored; a stopped run’s verdict reads did not meet.`, links: r.kind === 'evaluation' && r.agent ? [{ to: `/runs/new?kind=evaluation&agent=${encodeURIComponent(r.agent)}`, label: 'Test this version again' }] : [{ to: '/runs/new', label: 'New run' }] }
    case 'complete':
      return { text: `Complete, in ${r.quarter}. The run analysis is on this page.`, links: r.kind === 'evaluation' ? [{ to: '/runs', label: 'Every run' }] : [{ to: '/runs/new', label: 'New run' }] }
  }
}

/** Why the Run view offers no branch for this run; null when it does. */
export function branchNote(r: Run): string | null {
  if (canBranch(r)) return null
  if (r.residents !== undefined) return 'A run with Residents bound on a socket is not branched: its Residents answered live.'
  if (r.kind !== 'evaluation') return 'The account branches a closed evaluation run. A sample or train run is branched by its own agent after it hands a patient back, with the run’s token: branch over MCP, or POST /v1/session/branch.'
  if (r.status === 'complete') return null
  return 'An evaluation run is branched once it is complete.'
}
